import { useEffect, useState } from "react";
import { Check, Loader2, Pencil, X } from "lucide-react";
import { api, apiError } from "@/lib/api";

/**
 * BucketEditor — compact inline editor for an asset's bucket / GET set number.
 * Click the chip → the value becomes an input; save PATCHes the asset via the
 * admin endpoint. Used in the InspectionDetail header and the Asset drawer so
 * technicians can change the bucket on the fly the moment they discover the
 * machine is running with a different bucket than what's registered.
 */
export default function BucketEditor({
  assetId,
  value,
  onSaved,
  size = "sm", // 'sm' | 'md'
}) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value || "");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [ok, setOk] = useState(false);

  useEffect(() => setDraft(value || ""), [value]);

  const save = async () => {
    const next = String(draft || "").trim();
    if (next === String(value || "").trim()) { setEditing(false); return; }
    if (!assetId) { setErr("No asset id."); return; }
    setBusy(true); setErr("");
    try {
      await api.patch(`/admin/assets/${encodeURIComponent(assetId)}`, {
        bucket_number: next || null,
      });
      setOk(true);
      setEditing(false);
      setTimeout(() => setOk(false), 1400);
      onSaved?.(next);
    } catch (ex) {
      setErr(apiError(ex));
    } finally {
      setBusy(false);
    }
  };

  const cancel = () => {
    setDraft(value || "");
    setEditing(false);
    setErr("");
  };

  const isSm = size === "sm";
  const chipCls = isSm
    ? "text-[11px] px-2 py-0.5"
    : "text-xs px-2.5 py-1";

  if (!editing) {
    return (
      <button
        type="button"
        onClick={() => setEditing(true)}
        className={`inline-flex items-center gap-1.5 rounded-full font-mono-tight transition hover:brightness-110 ${chipCls}`}
        style={{
          background: "rgba(245, 130, 32, 0.14)",
          border: "1px solid rgba(245, 130, 32, 0.35)",
          color: "var(--primary)",
        }}
        title="Edit bucket / GET set"
        data-testid="bucket-editor-chip"
      >
        <span className="uppercase tracking-widest text-[9px] font-black opacity-80">Bucket</span>
        <span className="font-black">{value || "—"}</span>
        {ok ? <Check className="w-3 h-3" /> : <Pencil className="w-3 h-3 opacity-70" />}
      </button>
    );
  }

  return (
    <div className="inline-flex items-center gap-1" data-testid="bucket-editor-form">
      <span className="text-[9px] uppercase tracking-widest font-black text-muted">Bucket</span>
      <input
        autoFocus
        className="input !min-h-[28px] !py-0.5 !px-2 !w-24 text-xs font-mono-tight"
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === "Enter") save();
          if (e.key === "Escape") cancel();
        }}
        placeholder="B-04"
        maxLength={16}
        disabled={busy}
        data-testid="bucket-editor-input"
      />
      <button
        type="button"
        onClick={save}
        disabled={busy}
        className="btn btn-xs btn-primary !py-0.5 !px-1.5"
        data-testid="bucket-editor-save"
        title="Save"
      >
        {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <Check className="w-3 h-3" />}
      </button>
      <button
        type="button"
        onClick={cancel}
        disabled={busy}
        className="btn btn-xs btn-ghost !py-0.5 !px-1.5"
        title="Cancel"
        data-testid="bucket-editor-cancel"
      >
        <X className="w-3 h-3" />
      </button>
      {err && <span className="text-[10px] text-red-400 ml-1 max-w-[160px] truncate" title={err}>{err}</span>}
    </div>
  );
}
